import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, History, Loader2, RefreshCw } from "lucide-react";

type Row = {
  id: string;
  actor_id: string | null;
  mode: string;
  batch_id: string;
  failure_id: string | null;
  route_id: string | null;
  route_label: string | null;
  category: string | null;
  attempts_added: number | null;
  final_status: number | null;
  outcome: "resolved" | "still_failing" | "skipped_locked" | "route_missing" | "error" | "cancelled";
  error: string | null;
  triggered_at: string;
  completed_at: string | null;
};

const outcomeStyle: Record<Row["outcome"], string> = {
  resolved: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  still_failing: "bg-destructive/15 text-destructive",
  skipped_locked: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  route_missing: "bg-muted text-muted-foreground",
  error: "bg-destructive/15 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
};

// RFC 4180 CSV escaping — wrap in quotes if the value contains comma, quote,
// newline, or leading/trailing whitespace, and double any embedded quotes.
const csvCell = (v: unknown) => {
  if (v == null) return "";
  const s = String(v);
  if (/[",\r\n]/.test(s) || s !== s.trim()) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toCsv = (rows: Row[]) => {
  const header = [
    "triggered_at", "completed_at", "batch_id", "mode", "actor_id",
    "failure_id", "route_id", "route_label", "category",
    "attempts_added", "final_status", "outcome", "error",
  ];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([
      r.triggered_at, r.completed_at ?? "", r.batch_id, r.mode, r.actor_id ?? "",
      r.failure_id ?? "", r.route_id ?? "", r.route_label ?? "", r.category ?? "",
      r.attempts_added ?? "", r.final_status ?? "", r.outcome, r.error ?? "",
    ].map(csvCell).join(","));
  }
  return lines.join("\n");
};

// Per-batch rollup: who triggered it, which rows were attempted, and the
// aggregate final result — useful for change-management sign-off.
const toBatchSummaryCsv = (rows: Row[]) => {
  const header = [
    "batch_id", "mode", "actor_id", "triggered_at", "completed_at",
    "rows_attempted", "resolved", "still_failing", "skipped_locked",
    "cancelled", "route_missing", "errors", "failure_ids",
  ];
  const groups = new Map<string, Row[]>();
  for (const r of rows) {
    const arr = groups.get(r.batch_id) ?? [];
    arr.push(r);
    groups.set(r.batch_id, arr);
  }
  const lines = [header.join(",")];
  for (const [batchId, batchRows] of groups) {
    const first = batchRows[0];
    const times = batchRows.map((r) => new Date(r.triggered_at).getTime());
    const ends = batchRows.map((r) => r.completed_at ? new Date(r.completed_at).getTime() : NaN).filter((n) => !Number.isNaN(n));
    const count = (o: Row["outcome"]) => batchRows.filter((r) => r.outcome === o).length;
    lines.push([
      batchId,
      first.mode,
      first.actor_id ?? "",
      new Date(Math.min(...times)).toISOString(),
      ends.length ? new Date(Math.max(...ends)).toISOString() : "",
      batchRows.length,
      count("resolved"),
      count("still_failing"),
      count("skipped_locked"),
      count("cancelled"),
      count("route_missing"),
      count("error"),
      batchRows.map((r) => r.failure_id).filter(Boolean).join(" "),
    ].map(csvCell).join(","));
  }
  return lines.join("\n");
};

export function HelpdeskAlertRetryAuditPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [routeFilter, setRouteFilter] = useState<string>("all");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_retry_audit" as any)
      .select("id, actor_id, mode, batch_id, failure_id, route_id, route_label, category, attempts_added, final_status, outcome, error, triggered_at, completed_at")
      .order("triggered_at", { ascending: false })
      .limit(1000);
    setLoading(false);
    if (error) return toast.error(`Load retry audit: ${error.message}`);
    setRows((data ?? []) as unknown as Row[]);
  };

  useEffect(() => { void load(); }, []);

  const routeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.route_id) seen.set(r.route_id, r.route_label ?? r.route_id.slice(0, 8));
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [rows]);

  const filtered = useMemo(() => {
    const fromMs = from ? new Date(from).getTime() : null;
    const toMs = to ? new Date(to).getTime() + 24 * 3600 * 1000 : null; // inclusive day
    return rows.filter((r) => {
      const t = new Date(r.triggered_at).getTime();
      if (fromMs != null && t < fromMs) return false;
      if (toMs != null && t >= toMs) return false;
      if (routeFilter !== "all" && r.route_id !== routeFilter) return false;
      return true;
    });
  }, [rows, from, to, routeFilter]);

  const batches = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of filtered) {
      const arr = m.get(r.batch_id) ?? [];
      arr.push(r);
      m.set(r.batch_id, arr);
    }
    return m;
  }, [filtered]);

  const downloadCsv = (mode: "rows" | "batches") => {
    if (filtered.length === 0) {
      toast.info("No audit rows match the current filters.");
      return;
    }
    const csv = mode === "rows" ? toCsv(filtered) : toBatchSummaryCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    link.href = url;
    link.download = `helpdesk-retry-${mode === "rows" ? "audit-rows" : "batch-summary"}-${stamp}.csv`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${mode === "rows" ? filtered.length + " rows" : batches.size + " batches"}`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="size-4" /> Slack retry audit log
          </CardTitle>
          <CardDescription>
            Every retry attempt is recorded — actor, batch id, mode, target row, and outcome (including cancelled).
            Filter by date range and route, then export as row-level CSV or per-batch summary.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadCsv("rows")} disabled={filtered.length === 0}>
            <Download className="size-3.5 mr-1" /> Rows CSV ({filtered.length})
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadCsv("batches")} disabled={filtered.length === 0}>
            <Download className="size-3.5 mr-1" /> Batches CSV ({batches.size})
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-4 items-end">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Route</Label>
            <Select value={routeFilter} onValueChange={setRouteFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All routes</SelectItem>
                {routeOptions.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="py-6 text-center"><Loader2 className="size-4 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">No retry attempts match the current filters.</div>
        ) : (
          <div className="space-y-3">
            {Array.from(batches.entries()).map(([batchId, batchRows]) => {
              const first = batchRows[0];
              const resolved = batchRows.filter((r) => r.outcome === "resolved").length;
              const failing = batchRows.filter((r) => r.outcome === "still_failing" || r.outcome === "error" || r.outcome === "route_missing").length;
              const skipped = batchRows.filter((r) => r.outcome === "skipped_locked").length;
              const cancelled = batchRows.filter((r) => r.outcome === "cancelled").length;
              return (
                <div key={batchId} className="rounded-lg border border-border">
                  <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-xs">
                    <Badge variant="outline">{first.mode}</Badge>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      batch {batchId.slice(0, 8)}…
                    </span>
                    <span className="text-muted-foreground">
                      {new Date(first.triggered_at).toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">
                      actor {first.actor_id ? first.actor_id.slice(0, 8) + "…" : "system"}
                    </span>
                    <span className="ml-auto flex gap-1.5">
                      <Badge className={outcomeStyle.resolved} variant="secondary">✓ {resolved}</Badge>
                      <Badge className={outcomeStyle.still_failing} variant="secondary">✗ {failing}</Badge>
                      {skipped > 0 && (
                        <Badge className={outcomeStyle.skipped_locked} variant="secondary">⏸ {skipped}</Badge>
                      )}
                      {cancelled > 0 && (
                        <Badge className={outcomeStyle.cancelled} variant="secondary">⊘ {cancelled}</Badge>
                      )}
                    </span>
                  </div>
                  <div className="divide-y divide-border/50 text-xs">
                    {batchRows.map((r) => (
                      <div key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                        <Badge className={outcomeStyle[r.outcome]} variant="secondary">
                          {r.outcome.replace("_", " ")}
                        </Badge>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {r.failure_id ? r.failure_id.slice(0, 8) + "…" : "—"}
                        </span>
                        <span>{r.route_label ?? "(deleted)"}</span>
                        {r.category && <span className="text-muted-foreground">· {r.category}</span>}
                        {r.final_status != null && (
                          <span className="font-mono text-muted-foreground">HTTP {r.final_status}</span>
                        )}
                        {r.attempts_added != null && r.attempts_added > 0 && (
                          <span className="text-muted-foreground">+{r.attempts_added} attempt{r.attempts_added === 1 ? "" : "s"}</span>
                        )}
                        {r.error && (
                          <span className="ml-auto max-w-md truncate text-muted-foreground" title={r.error}>
                            {r.error}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskAlertRetryAuditPanel;
